import { useQuery } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Target, ShieldCheck, Loader2 } from "lucide-react";
import { fetchScoreboard } from "@/lib/api";

export default function ScoreboardDialog() {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          data-testid="scoreboard-btn"
          className="inline-flex items-center gap-1.5 rounded border border-amber-500/30 px-2 py-0.5 font-mono-tel text-[10px] font-bold text-amberx transition-colors hover:bg-amber-500/15"
        >
          <Target className="h-3 w-3" /> SCOREBOARD
        </button>
      </DialogTrigger>
      <DialogContent className="max-h-[86vh] max-w-2xl overflow-y-auto border-amber-500/25 bg-[#0a0f05] text-slate-200">
        <DialogHeader>
          <DialogTitle className="font-display text-lg font-extrabold tracking-tight text-white">
            Model vs Reality — verified accuracy
          </DialogTitle>
        </DialogHeader>
        <Body />
      </DialogContent>
    </Dialog>
  );
}

function Body() {
  const { data, isLoading } = useQuery({ queryKey: ["scoreboard"], queryFn: fetchScoreboard, staleTime: 30 * 60 * 1000 });

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-10 text-slate-400">
        <Loader2 className="h-4 w-4 animate-spin" /> Verifying against the last satellite passes…
      </div>
    );
  }
  if (!data?.available) {
    return <div className="py-8 text-sm text-slate-400">Not enough satellite history to verify yet — check back after the next data cycle.</div>;
  }

  const days = data.days || [];
  const maxKm = Math.max(1, ...days.flatMap((d) => [d.our_km, d.persist_km]));
  const beats = data.mean_error_km <= data.persistence_error_km;

  return (
    <div className="space-y-4">
      <p className="text-[12px] leading-relaxed text-slate-400">
        We publish our own error bars. Each day, the model advects the <span className="text-emerald-300">real</span> AFAI
        belt forward and is scored against the <span className="text-emerald-300">actual next satellite pass</span> —
        compared to a persistence (no-movement) baseline. Nothing here is asserted; it is measured.
      </p>

      <div className="grid grid-cols-3 gap-2">
        <Stat label="Mean position error" value={`±${data.mean_error_km} km`} sub={`@ +${data.lead_hours}h`} tone="cyan" />
        <Stat label="Persistence baseline" value={`±${data.persistence_error_km} km`} sub="no-movement" tone="slate" />
        <Stat label="Days model ≤ baseline" value={`${data.wins}/${data.n}`} sub="verified days" tone={beats ? "emerald" : "amber"} />
      </div>

      <div>
        <div className="eyebrow mb-2 text-slate-500">Daily verification — model (cyan) vs persistence (grey)</div>
        <div className="space-y-1.5" data-testid="scoreboard-days">
          {days.map((d) => (
            <div key={d.date} className="flex items-center gap-2">
              <span className="w-16 shrink-0 font-mono-tel text-[10px] text-slate-500">{d.date?.slice(5)}</span>
              <div className="flex-1 space-y-0.5">
                <Bar km={d.our_km} max={maxKm} color="#22d3ee" />
                <Bar km={d.persist_km} max={maxKm} color="#475569" />
              </div>
              <span className={`w-14 shrink-0 text-right font-mono-tel text-[10px] ${d.our_km <= d.persist_km ? "text-emerald-300" : "text-slate-500"}`}>
                {d.our_km <= d.persist_km ? "match+" : ""}{d.our_km}km
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-lg border border-amber-500/20 bg-amber-500/[0.04] p-3">
        <div className="mb-1 flex items-center gap-1.5 eyebrow text-amber-300/90">
          <ShieldCheck className="h-3.5 w-3.5" /> Honest read
        </div>
        <p className="text-[11.5px] leading-snug text-slate-300">
          At whole-basin scale the physical drift tracks the observed belt to within roughly {data.mean_error_km} km at
          +{data.lead_hours}h — essentially <b>on par with persistence</b> ({data.wins}/{data.n} days at or better than the
          no-movement baseline). That is expected and we state it plainly: day-to-day the belt changes mostly by growth,
          decay and satellite coverage, not bulk translation, so no model meaningfully beats persistence here — and we do
          not pretend to. What the platform delivers is a <b>verified real-time nowcast</b> plus a transparent, physically-bounded
          per-island arrival projection with honest uncertainty. This scoreboard stays live so the track record is un-gameable.
        </p>
      </div>
    </div>
  );
}

function Stat({ label, value, sub, tone }) {
  const tones = { cyan: "text-cyanx", emerald: "text-emerald-300", amber: "text-amberx", slate: "text-slate-300" };
  return (
    <div className="rounded-lg border border-white/10 bg-black/30 p-2.5">
      <div className="eyebrow text-slate-500">{label}</div>
      <div className={`font-mono-tel text-lg font-bold ${tones[tone] || "text-slate-200"}`}>{value}</div>
      <div className="font-mono-tel text-[10px] text-slate-500">{sub}</div>
    </div>
  );
}

function Bar({ km, max, color }) {
  const pct = Math.max(3, Math.round((km / max) * 100));
  return (
    <div className="h-2 w-full overflow-hidden rounded-sm bg-white/5">
      <div className="h-full rounded-sm" style={{ width: `${pct}%`, background: color }} />
    </div>
  );
}
